import * as React from 'react'
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Collapse,
    CircularProgress,
    Divider,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded'
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded'
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import HandymanRoundedIcon from '@mui/icons-material/HandymanRounded'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import useAutoTranslate from '../hooks/useAutoTranslate'

const TEMPLATE_STORAGE_KEY = 'attackTemplateBuilder'
const SLOT_PICKER_FAVORITES_KEY = 'attackTemplateSlotPickerFavorites'

const EMPTY_CATALOG = {
    attackTroops: [],
    defenseTroops: [],
    attackTools: [],
    defenseTools: []
}

const MAX_TEMPLATE_NAME_LENGTH = 50
const MAX_TEMPLATE_DESCRIPTION_LENGTH = 500

const loadSlotPickerFavorites = () => {
    if (typeof window === 'undefined')
        return []

    try {
        const rawValue = window.localStorage.getItem(SLOT_PICKER_FAVORITES_KEY)
        const parsedValue = JSON.parse(rawValue ?? '[]')
        return Array.isArray(parsedValue) ?
            parsedValue.map(value => String(value)).filter(Boolean) :
            []
    }
    catch {
        return []
    }
}

const saveSlotPickerFavorites = favoriteKeys => {
    if (typeof window === 'undefined')
        return

    try {
        window.localStorage.setItem(SLOT_PICKER_FAVORITES_KEY, JSON.stringify(favoriteKeys))
    }
    catch { }
}

const createWaveId = () => `wave-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

const cloneValue = value => {
    if (typeof structuredClone === 'function')
        return structuredClone(value)

    return JSON.parse(JSON.stringify(value))
}

const areSimpleArraysEqual = (left, right) =>
    left.length === right.length && left.every((entry, index) => entry === right[index])

const areInventoryArraysEqual = (left, right) => {
    const leftItems = Array.isArray(left) ? left : []
    const rightItems = Array.isArray(right) ? right : []

    if (leftItems.length !== rightItems.length)
        return false

    return leftItems.every((leftEntry, index) => {
        const rightEntry = rightItems[index]
        return String(leftEntry?.value ?? leftEntry?.id ?? '') === String(rightEntry?.value ?? rightEntry?.id ?? '') &&
            String(leftEntry?.amount ?? '') === String(rightEntry?.amount ?? '') &&
            String(leftEntry?.label ?? '') === String(rightEntry?.label ?? '')
    })
}

const createEmptySlot = () => ({
    id: '',
    amount: '',
    level: ''
})

const normalizeSlot = value => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        return {
            id: String(value.id ?? value.value ?? ''),
            amount: String(value.amount ?? ''),
            level: String(value.level ?? '')
        }
    }

    return {
        id: String(value ?? ''),
        amount: '',
        level: ''
    }
}

const createLane = (toolCount, troopCount) => ({
    tools: Array.from({ length: toolCount }, createEmptySlot),
    troops: Array.from({ length: troopCount }, createEmptySlot)
})

const createWave = label => ({
    id: createWaveId(),
    name: label,
    left: createLane(2, 2),
    middle: createLane(3, 6),
    right: createLane(2, 2)
})

const createDefaultTemplate = () => {
    const firstWave = createWave('Wave 1')

    return {
        name: '',
        presetSource: '',
        description: '',
        defaultWaveId: firstWave.id,
        waves: [firstWave],
        supportTools: Array.from({ length: 3 }, createEmptySlot),
        supportWave: Array.from({ length: 8 }, createEmptySlot)
    }
}

const normalizeSlotArray = (value, length) => {
    const source = Array.isArray(value) ? value : []
    return Array.from({ length }, (_, index) => normalizeSlot(source[index]))
}

const normalizeLane = (value, toolCount, troopCount) => ({
    tools: normalizeSlotArray(value?.tools, toolCount),
    troops: normalizeSlotArray(value?.troops, troopCount)
})

const normalizeWave = (value, index) => ({
    id: value?.id || createWaveId(),
    name: value?.name || `Wave ${index + 1}`,
    left: normalizeLane(value?.left, 2, 2),
    middle: normalizeLane(value?.middle, 3, 6),
    right: normalizeLane(value?.right, 2, 2)
})

export const normalizeAttackTemplateDraft = value => {
    const base = value && typeof value === 'object' ? value : {}
    const fallbackTemplate = createDefaultTemplate()
    const waves = Array.isArray(base.waves) && base.waves.length > 0 ?
        base.waves.map(normalizeWave) :
        fallbackTemplate.waves.map(normalizeWave)

    const defaultWaveId = waves.some(wave => wave.id === base.defaultWaveId) ?
        base.defaultWaveId :
        waves[0].id

    return {
        name: String(base.name ?? fallbackTemplate.name),
        presetSource: base.presetSource ?? fallbackTemplate.presetSource,
        description: String(base.description ?? fallbackTemplate.description),
        defaultWaveId,
        waves,
        supportTools: normalizeSlotArray(base.supportTools ?? fallbackTemplate.supportTools, 3),
        supportWave: normalizeSlotArray(base.supportWave ?? fallbackTemplate.supportWave, 8)
    }
}

const normalizeTemplate = normalizeAttackTemplateDraft

const areTemplatesEqual = (left, right) =>
    JSON.stringify(normalizeTemplate(left)) === JSON.stringify(normalizeTemplate(right))

const createUnknownOption = (value, groupLabel, t = value => value) => ({
    value: String(value),
    label: `${t('Saved ID')} ${value}`,
    group: `${groupLabel} / ${t('Saved')}`,
    category: 'saved',
    statLine: t('Previously saved value'),
    searchText: String(value)
})

const toOptionList = catalog => ({
    troops: [...catalog.attackTroops, ...catalog.defenseTroops],
    tools: [...catalog.attackTools, ...catalog.defenseTools]
})

const buildAvailableInventoryMap = inventory => {
    const nextMap = new Map()

    for (const entry of Array.isArray(inventory) ? inventory : []) {
        const value = String(entry?.value ?? entry?.id ?? '').trim()
        const amount = Math.max(0, Math.floor(Number(entry?.amount ?? 0)))
        if (!value || amount <= 0)
            continue

        nextMap.set(value, amount)
    }

    return nextMap
}

const collectSelectedTemplateIds = template => {
    const selectedIds = {
        troops: new Set(),
        tools: new Set()
    }
    const rememberSlot = (slot, kind) => {
        const value = String(slot?.id ?? '').trim()
        if (!value)
            return

        selectedIds[kind].add(value)
    }

    for (const wave of template?.waves ?? []) {
        for (const lane of [wave?.left, wave?.middle, wave?.right]) {
            for (const slot of lane?.troops ?? [])
                rememberSlot(slot, 'troops')
            for (const slot of lane?.tools ?? [])
                rememberSlot(slot, 'tools')
        }
    }

    for (const slot of template?.supportWave ?? [])
        rememberSlot(slot, 'troops')
    for (const slot of template?.supportTools ?? [])
        rememberSlot(slot, 'tools')

    return selectedIds
}

const filterCatalogGroupByAvailability = (entries, availableInventoryMap, selectedIds, enforceAvailabilityFilter) => {
    if (!enforceAvailabilityFilter)
        return Array.isArray(entries) ? entries : []

    return (Array.isArray(entries) ? entries : []).filter(entry => {
        const value = String(entry?.value ?? '').trim()
        return availableInventoryMap.has(value) || selectedIds.has(value)
    })
}

const filterCatalogByAvailability = (catalog, availableInventoryMap, selectedIds, enforceAvailabilityFilter) => ({
    attackTroops: filterCatalogGroupByAvailability(catalog.attackTroops, availableInventoryMap, selectedIds.troops, enforceAvailabilityFilter),
    defenseTroops: filterCatalogGroupByAvailability(catalog.defenseTroops, availableInventoryMap, selectedIds.troops, enforceAvailabilityFilter),
    attackTools: filterCatalogGroupByAvailability(catalog.attackTools, availableInventoryMap, selectedIds.tools, enforceAvailabilityFilter),
    defenseTools: filterCatalogGroupByAvailability(catalog.defenseTools, availableInventoryMap, selectedIds.tools, enforceAvailabilityFilter)
})

const attachOwnedAmount = (options, availableInventoryMap) =>
    (Array.isArray(options) ? options : []).map(option => ({
        ...option,
        ownedAmount: Math.max(0, Math.floor(Number(availableInventoryMap.get(String(option?.value ?? '').trim()) ?? 0)))
    }))

const normalizeSearchText = value =>
    String(value ?? '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')

const getExtendedOptions = (options, normalizedValue, groupLabel, t = value => value) => {
    if (!normalizedValue)
        return options

    return options.some(option => option.value === normalizedValue) ?
        options :
        [createUnknownOption(normalizedValue, groupLabel, t), ...options]
}

const filterCatalogOptions = (candidateOptions, state) => {
    const query = normalizeSearchText(state.inputValue)
    if (!query)
        return candidateOptions

    return candidateOptions.filter(option =>
        normalizeSearchText(option.searchText ?? option.label).includes(query))
}

const extractLevelValue = option => {
    const match = String(option?.statLine ?? '').match(/Level\s+([0-9]+)/i)
    return match ? match[1] : ''
}

const resolveSlotLevelValue = (slot, option) => {
    const manualLevel = String(slot?.level ?? '').replace(/[^\d]/g, '')
    return manualLevel || extractLevelValue(option)
}

const resolveSlotLevelLabel = (slot, option, t = value => value) => {
    const levelValue = resolveSlotLevelValue(slot, option)
    return levelValue ? `${t('Level')} ${levelValue}` : `${t('Level')} --`
}

const buildSlotMetaLabel = (option, slot, kind, t = value => value) => {
    if (!option)
        return t('Open editor to search by name or ID')

    const baseLabel = kind === 'troop' ? resolveSlotLevelLabel(slot, option, t) : option.statLine
    return slot?.amount ? `${baseLabel} | ${t('Qty')} ${slot.amount}` : baseLabel
}

function ItemIconFrame({
    iconUrl,
    itemType = 'troop',
    badge = '',
    amount = '',
    size = 100
}) {
    const frameSize = Math.max(Number(size) || 100, 100)
    const iconSize = frameSize
    const fallbackIcon = itemType === 'tool' ?
        <HandymanRoundedIcon sx={{ fontSize: Math.max(34, frameSize - 24), color: '#d6e2f7' }} /> :
        <GroupsRoundedIcon sx={{ fontSize: Math.max(34, frameSize - 24), color: '#d6e2f7' }} />

    return (
        <Box
            sx={{
                position: 'relative',
                width: frameSize,
                height: frameSize,
                borderRadius: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                overflow: 'hidden',
                border: 0,
                backgroundColor: 'transparent',
                boxShadow: 'none'
            }}
        >
            {iconUrl ? (
                <Box
                    component="img"
                    src={iconUrl}
                    alt=""
                    loading="lazy"
                    sx={{
                        width: iconSize,
                        height: iconSize,
                        objectFit: 'contain',
                        imageRendering: 'auto',
                        pointerEvents: 'none'
                    }}
                />
            ) : fallbackIcon}
            {badge ? (
                <Box
                    component="span"
                    sx={{
                        position: 'absolute',
                        top: 2,
                        left: 2,
                        minWidth: 16,
                        height: 16,
                        px: 0.35,
                        borderRadius: '50%',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#2d8edb',
                        border: '1px solid rgba(255,255,255,0.92)',
                        color: '#ffffff',
                        fontWeight: 800,
                        fontSize: '0.66rem',
                        lineHeight: 1
                    }}
                >
                    {badge}
                </Box>
            ) : null}
            {amount ? (
                <Box
                    component="span"
                    sx={{
                        position: 'absolute',
                        right: 0,
                        bottom: 0,
                        left: 0,
                        minHeight: 13,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(228, 201, 168, 0.95)',
                        borderTop: '1px solid rgba(125, 92, 58, 0.42)',
                        color: '#23170e',
                        fontWeight: 800,
                        fontSize: '0.62rem',
                        lineHeight: 1
                    }}
                >
                    {amount}
                </Box>
            ) : null}
        </Box>
    )
}

const buildSlotPickerEntries = pickerOptions => {
    const nextEntries = []
    const seenKeys = new Set()

    const appendEntries = (entries, itemType) => {
        for (const option of Array.isArray(entries) ? entries : []) {
            const value = String(option?.value ?? '').trim()
            if (!value)
                continue

            const itemKey = `${itemType}:${value}`
            if (seenKeys.has(itemKey))
                continue

            seenKeys.add(itemKey)
            nextEntries.push({
                ...option,
                key: itemKey,
                value,
                itemType,
                searchText: String(option?.searchText ?? `${option?.label ?? ''} ${value}`)
            })
        }
    }

    appendEntries(pickerOptions?.troops, 'troop')
    appendEntries(pickerOptions?.tools, 'tool')

    return nextEntries
}

function PickerFilterButton({ icon, label, count, active, onClick }) {
    return (
        <Button
            variant="outlined"
            onClick={onClick}
            startIcon={icon}
            sx={{
                minHeight: 36,
                px: 1.35,
                borderRadius: '10px',
                borderColor: active ? 'rgba(112, 136, 174, 0.56)' : 'rgba(112, 136, 174, 0.34)',
                backgroundColor: active ? 'rgba(74, 91, 120, 0.72)' : 'rgba(42, 54, 78, 0.56)',
                color: '#eef4ff',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.96rem',
                gap: 0.8,
                '&:hover': {
                    borderColor: 'rgba(112, 136, 174, 0.58)',
                    backgroundColor: active ? 'rgba(82, 98, 127, 0.78)' : 'rgba(50, 63, 88, 0.68)'
                }
            }}
        >
            <Box component="span">{label}</Box>
            <Box
                component="span"
                sx={{
                    px: 0.75,
                    minWidth: 34,
                    minHeight: 24,
                    borderRadius: '8px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    color: '#aebad3',
                    fontWeight: 800,
                    fontSize: '0.95rem'
                }}
            >
                {count}
            </Box>
        </Button>
    )
}

function TroopToolPickerDialog({
    open,
    options,
    onClose,
    onSelect,
    appLanguage = 'en'
}) {
    const t = useAutoTranslate(appLanguage, [
        'Troop & Tool Picker',
        'Search by name or ID...',
        'Favorites',
        'Clear',
        'All',
        'Troops',
        'Tools',
        'results',
        'Tool',
        'Troop',
        'Click to select • Favorite to bookmark • Type an ID manually in the input field',
        'No results found'
    ])
    const [query, setQuery] = React.useState('')
    const [filterValue, setFilterValue] = React.useState('all')
    const [favoritesOnly, setFavoritesOnly] = React.useState(false)
    const [favoriteKeys, setFavoriteKeys] = React.useState(() => loadSlotPickerFavorites())
    const favoriteKeySet = React.useMemo(() => new Set(favoriteKeys), [favoriteKeys])

    React.useEffect(() => {
        if (!open)
            return

        setQuery('')
        setFilterValue('all')
        setFavoritesOnly(false)
    }, [open])

    React.useEffect(() => {
        saveSlotPickerFavorites(favoriteKeys)
    }, [favoriteKeys])

    const allEntries = React.useMemo(() => buildSlotPickerEntries(options), [options])
    const counts = React.useMemo(() => ({
        all: allEntries.length,
        troops: allEntries.filter(entry => entry.itemType === 'troop').length,
        tools: allEntries.filter(entry => entry.itemType === 'tool').length
    }), [allEntries])

    const filteredEntries = React.useMemo(() => {
        const normalizedQuery = normalizeSearchText(query)

        return allEntries
            .filter(entry => {
                if (filterValue === 'troops' && entry.itemType !== 'troop')
                    return false
                if (filterValue === 'tools' && entry.itemType !== 'tool')
                    return false
                if (favoritesOnly && !favoriteKeySet.has(entry.key))
                    return false
                if (!normalizedQuery)
                    return true

                return normalizeSearchText(`${entry.label} ${entry.value} ${entry.searchText}`).includes(normalizedQuery)
            })
            .sort((left, right) => {
                const leftFavorite = favoriteKeySet.has(left.key)
                const rightFavorite = favoriteKeySet.has(right.key)
                if (leftFavorite !== rightFavorite)
                    return leftFavorite ? -1 : 1

                return String(left.label ?? '').localeCompare(String(right.label ?? ''))
            })
    }, [allEntries, favoriteKeySet, favoritesOnly, filterValue, query])

    const toggleFavorite = React.useCallback(itemKey => {
        setFavoriteKeys(currentKeys =>
            currentKeys.includes(itemKey) ?
                currentKeys.filter(currentKey => currentKey !== itemKey) :
                [...currentKeys, itemKey]
        )
    }, [])

    const handleClear = () => {
        setQuery('')
        setFilterValue('all')
        setFavoritesOnly(false)
    }

    const resultCountLabel = `${filteredEntries.length} ${t('results')}`

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: 'min(calc(100vw - 20px), 566px)',
                    maxWidth: '566px',
                    minHeight: '724px',
                    maxHeight: 'min(calc(100vh - 20px), 724px)',
                    borderRadius: '14px',
                    overflow: 'hidden',
                    background: 'linear-gradient(180deg, rgba(39,51,74,0.98) 0%, rgba(31,43,66,0.98) 100%)',
                    border: '1px solid rgba(122, 143, 180, 0.32)',
                    boxShadow: '0 26px 60px rgba(0,0,0,0.42)'
                }
            }}
            BackdropProps={{
                sx: {
                    backdropFilter: 'blur(10px)',
                    backgroundColor: 'rgba(3, 8, 18, 0.68)'
                }
            }}
        >
            <DialogTitle sx={{ px: 2.2, pt: 2.05, pb: 1.25 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <SearchRoundedIcon sx={{ fontSize: 28, color: '#f4f7ff' }} />
                        <Typography sx={{ fontSize: '1.16rem', fontWeight: 800, color: '#f5f7ff' }}>
                            {t('Troop & Tool Picker')}
                        </Typography>
                    </Stack>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            color: '#9aa7c2',
                            width: 30,
                            height: 30
                        }}
                    >
                        <CloseRoundedIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ px: 2.2, pt: 0, pb: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Stack direction="row" spacing={1.1} sx={{ mb: 1.5 }}>
                    <TextField
                        fullWidth
                        placeholder={t('Search by name or ID...')}
                        value={query}
                        onChange={event => setQuery(event.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                minHeight: 42,
                                borderRadius: '10px',
                                backgroundColor: 'rgba(28, 41, 65, 0.9)',
                                '& fieldset': {
                                    borderColor: 'rgba(57, 121, 248, 0.95)',
                                    borderWidth: '2px'
                                },
                                '&:hover fieldset': {
                                    borderColor: 'rgba(74, 134, 255, 0.96)'
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'rgba(74, 134, 255, 0.96)'
                                }
                            },
                            '& .MuiInputBase-input': {
                                color: '#edf3ff',
                                fontSize: '0.98rem',
                                fontWeight: 600
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchRoundedIcon sx={{ color: '#a8b4cd', fontSize: 24 }} />
                                </InputAdornment>
                            )
                        }}
                    />
                    <Button
                        variant="outlined"
                        startIcon={favoriteKeySet.size > 0 || favoritesOnly ?
                            <FavoriteRoundedIcon sx={{ color: favoritesOnly ? '#ff8ea0' : '#ffffff' }} /> :
                            <FavoriteBorderRoundedIcon />}
                        onClick={() => setFavoritesOnly(current => !current)}
                        sx={{
                            minWidth: 130,
                            minHeight: 42,
                            borderRadius: '10px',
                            borderColor: favoritesOnly ? 'rgba(255, 113, 141, 0.72)' : 'rgba(112, 136, 174, 0.34)',
                            backgroundColor: favoritesOnly ? 'rgba(255, 113, 141, 0.14)' : 'rgba(42, 54, 78, 0.56)',
                            color: '#f3f6ff',
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.96rem',
                            '&:hover': {
                                borderColor: favoritesOnly ? 'rgba(255, 113, 141, 0.82)' : 'rgba(112, 136, 174, 0.5)',
                                backgroundColor: favoritesOnly ? 'rgba(255, 113, 141, 0.2)' : 'rgba(50, 63, 88, 0.7)'
                            }
                        }}
                    >
                        {t('Favorites')}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<CloseRoundedIcon />}
                        onClick={handleClear}
                        sx={{
                            minWidth: 110,
                            minHeight: 42,
                            borderRadius: '10px',
                            backgroundColor: '#ff3345',
                            color: '#ffffff',
                            textTransform: 'none',
                            fontWeight: 800,
                            fontSize: '0.96rem',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: '#ff4858',
                                boxShadow: 'none'
                            }
                        }}
                    >
                        {t('Clear')}
                    </Button>
                </Stack>

                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.55 }}>
                    <Stack direction="row" spacing={0.85}>
                        <PickerFilterButton
                            icon={<FilterAltRoundedIcon sx={{ fontSize: 19 }} />}
                            label={t('All')}
                            count={counts.all}
                            active={filterValue === 'all'}
                            onClick={() => setFilterValue('all')}
                        />
                        <PickerFilterButton
                            icon={<GroupsRoundedIcon sx={{ fontSize: 19 }} />}
                            label={t('Troops')}
                            count={counts.troops}
                            active={filterValue === 'troops'}
                            onClick={() => setFilterValue('troops')}
                        />
                        <PickerFilterButton
                            icon={<HandymanRoundedIcon sx={{ fontSize: 19 }} />}
                            label={t('Tools')}
                            count={counts.tools}
                            active={filterValue === 'tools'}
                            onClick={() => setFilterValue('tools')}
                        />
                    </Stack>
                    <Typography sx={{ color: '#b3bfd7', fontWeight: 700, fontSize: '0.98rem' }}>
                        {resultCountLabel}
                    </Typography>
                </Stack>

                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        borderRadius: '12px',
                        border: '1px solid rgba(123, 147, 186, 0.28)',
                        backgroundColor: 'rgba(31, 43, 67, 0.92)',
                        overflowY: 'auto',
                        overflowX: 'hidden'
                    }}
                >
                    {filteredEntries.length > 0 ? filteredEntries.map((entry, index) => {
                        const isFavorite = favoriteKeySet.has(entry.key)
                        const categoryLabel = entry.itemType === 'tool' ? t('Tool') : t('Troop')

                        return (
                            <Box
                                key={entry.key}
                                role="button"
                                tabIndex={0}
                                onClick={() => {
                                    onSelect?.(entry.value)
                                    onClose?.()
                                }}
                                onKeyDown={event => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault()
                                        onSelect?.(entry.value)
                                        onClose?.()
                                    }
                                }}
                                sx={{
                                    px: 2.15,
                                    py: 1.6,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1.2,
                                    cursor: 'pointer',
                                    borderBottom: index < filteredEntries.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.03)'
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: 1 }}>
                                    <ItemIconFrame
                                        iconUrl={entry.iconUrl}
                                        itemType={entry.itemType}
                                        badge={entry.itemType === 'troop' && Number(entry.troopLevel) > 0 ? String(entry.troopLevel) : ''}
                                        size={52}
                                    />
                                    <Box sx={{ minWidth: 0 }}>
                                        <Stack direction="row" alignItems="center" spacing={0.9} sx={{ mb: 0.6 }}>
                                            <Typography sx={{ color: '#5ea3ff', fontWeight: 800, fontSize: '1.01rem' }}>
                                                {`#${index + 1}`}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    px: 0.95,
                                                    minHeight: 28,
                                                    borderRadius: '10px',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    border: '1px solid rgba(130, 147, 177, 0.45)',
                                                    color: '#d6deec',
                                                    fontWeight: 700,
                                                    fontSize: '0.94rem'
                                                }}
                                            >
                                                {categoryLabel}
                                            </Box>
                                        </Stack>

                                        <Typography sx={{ color: '#f5f7ff', fontWeight: 700, fontSize: '1.14rem', lineHeight: 1.18 }}>
                                            {entry.label}
                                        </Typography>
                                    </Box>
                                </Box>

                                <IconButton
                                    onClick={event => {
                                        event.stopPropagation()
                                        toggleFavorite(entry.key)
                                    }}
                                    sx={{
                                        color: isFavorite ? '#ff6e82' : '#9aa6bf',
                                        width: 34,
                                        height: 34
                                    }}
                                >
                                    {isFavorite ? <FavoriteRoundedIcon /> : <FavoriteBorderRoundedIcon />}
                                </IconButton>
                            </Box>
                        )
                    }) : (
                        <Box sx={{ px: 2.2, py: 3.2 }}>
                            <Typography sx={{ color: '#b1bdd5', fontWeight: 600 }}>
                                {t('No results found')}
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Box
                    sx={{
                        mt: 1.45,
                        px: 2.2,
                        py: 1.45,
                        mx: -2.2,
                        borderTop: '1px solid rgba(255,255,255,0.08)'
                    }}
                >
                    <Typography sx={{ color: '#7f8aa3', fontSize: '0.94rem' }}>
                        {t('Click to select • Favorite to bookmark • Type an ID manually in the input field')}
                    </Typography>
                </Box>
            </DialogContent>
        </Dialog>
    )
}

function SlotEditor({
    slot,
    options,
    pickerOptions,
    accentColor,
    loading,
    kind,
    onChange,
    onOpenEditor,
    appLanguage = 'en',
    displayMode = 'detailed',
    placeholder
}) {
    const normalizedSlot = React.useMemo(() => normalizeSlot(slot), [slot])
    const normalizedValue = String(normalizedSlot.id ?? '')
    const t = useAutoTranslate(appLanguage, [
        'Saved ID',
        'Saved',
        'Previously saved value',
        'Attack',
        'Defense',
        'Level',
        'Qty',
        'Pick troop/tool ID',
        'Open editor to search by name or ID',
        'Search by troop name or ID and the troop level will appear here.',
        'Search by tool name or ID and the selected tool details will appear here.',
        `Pick ${kind}`,
        `Pick ${kind} ID`,
        `Loading ${kind}s...`,
        `No matching ${kind}s`,
        `Selected ${kind}`,
        `${kind} preview`,
        `Pick ${kind} to see details`,
        'Edit Slot',
        'Amount',
        'Cancel',
        'Remove',
        'Save',
        'You can correct the troop level here if the detected value is wrong.'
    ])
    const extendedOptions = React.useMemo(() =>
        getExtendedOptions(options, normalizedValue, kind === 'troop' ? 'Troops' : 'Tools', t),
    [kind, normalizedValue, options, t])
    const selectedOption = React.useMemo(() =>
        extendedOptions.find(option => option.value === normalizedValue) ?? null,
    [extendedOptions, normalizedValue])
    const [open, setOpen] = React.useState(false)
    const [pickerOpen, setPickerOpen] = React.useState(false)
    const [draftSlot, setDraftSlot] = React.useState(normalizedSlot)

    React.useEffect(() => {
        setDraftSlot(normalizedSlot)
    }, [normalizedSlot])

    React.useEffect(() => {
        if (!open)
            setPickerOpen(false)
    }, [open])

    const draftOptions = React.useMemo(() =>
        getExtendedOptions(options, draftSlot.id, kind === 'troop' ? 'Troops' : 'Tools', t),
    [draftSlot.id, kind, options, t])
    const draftOption = React.useMemo(() =>
        draftOptions.find(option => option.value === draftSlot.id) ?? null,
    [draftOptions, draftSlot.id])

    const openEditor = async () => {
        setDraftSlot(normalizedSlot)
        try {
            await onOpenEditor?.()
        }
        catch { }
        setOpen(true)
    }

    const closeEditor = () => {
        setDraftSlot(normalizedSlot)
        setOpen(false)
    }

    const openPicker = async () => {
        try {
            await onOpenEditor?.()
        }
        catch { }

        setPickerOpen(true)
    }

    const saveEditor = () => {
        onChange(normalizeSlot(draftSlot))
        setOpen(false)
    }

    const removeValue = () => {
        onChange(createEmptySlot())
        setOpen(false)
    }

    const compactLabel = placeholder || (kind === 'tool' ? 'T' : 'U')
    const compactValueLabel = normalizedSlot.id ? String(normalizedSlot.id) : compactLabel
    const compactAmountLabel = normalizedSlot.id && normalizedSlot.amount ? `x${normalizedSlot.amount}` : ''
    const compactLevelBadge = kind === 'troop' ? resolveSlotLevelValue(normalizedSlot, selectedOption) : ''

    return (
        <>
            {displayMode === 'compact' ? (
                <Button
                    variant="outlined"
                    onClick={openEditor}
                    sx={{
                        minWidth: 86,
                        width: '100%',
                        minHeight: normalizedSlot.id ? 62 : 40,
                        height: normalizedSlot.id ? 62 : 40,
                        justifyContent: 'center',
                        textAlign: 'center',
                        borderRadius: '7px',
                        px: 0.65,
                        py: normalizedSlot.id ? 0.45 : 0.4,
                        borderColor: normalizedSlot.id ? 'rgba(141, 118, 87, 0.9)' : accentColor,
                        backgroundColor: normalizedSlot.id ? 'rgba(54, 42, 29, 0.32)' : 'rgba(7, 14, 26, 0.45)',
                        color: '#dce7fb',
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        lineHeight: 1,
                        overflow: 'visible',
                        '&:hover': {
                            borderColor: normalizedSlot.id ? '#c09a61' : accentColor,
                            backgroundColor: normalizedSlot.id ? 'rgba(77, 58, 35, 0.42)' : 'rgba(17, 27, 46, 0.74)'
                        }
                    }}
                >
                    {normalizedSlot.id ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.55, width: '100%', minWidth: 0 }}>
                            <ItemIconFrame
                                iconUrl={selectedOption?.iconUrl}
                                itemType={kind}
                                badge={compactLevelBadge}
                                amount={compactAmountLabel}
                                size={48}
                            />
                            <Typography
                                component="span"
                                sx={{
                                    minWidth: 0,
                                    maxWidth: 30,
                                    color: 'rgba(238, 244, 255, 0.84)',
                                    fontSize: '0.66rem',
                                    fontWeight: 800,
                                    lineHeight: 1.05,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}
                            >
                                {compactValueLabel}
                            </Typography>
                        </Box>
                    ) : compactLabel}
                </Button>
            ) : displayMode === 'support' ? (
                <Button
                    fullWidth
                    variant="outlined"
                    onClick={openEditor}
                    sx={{
                        minHeight: 40,
                        justifyContent: 'space-between',
                        textAlign: 'left',
                        borderRadius: '8px',
                        px: 1.2,
                        py: 0.45,
                        borderColor: accentColor,
                        backgroundColor: 'rgba(15, 23, 40, 0.72)',
                        color: '#dce7fb',
                        textTransform: 'none',
                        '&:hover': {
                            borderColor: accentColor,
                            backgroundColor: 'rgba(21, 31, 52, 0.88)'
                        }
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85, minWidth: 0 }}>
                        {normalizedSlot.id ? (
                            <ItemIconFrame
                                iconUrl={selectedOption?.iconUrl}
                                itemType={kind}
                                badge={kind === 'troop' ? resolveSlotLevelValue(normalizedSlot, selectedOption) : ''}
                                amount={compactAmountLabel}
                                size={34}
                            />
                        ) : null}
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 600,
                                color: normalizedSlot.id ? '#dce7fb' : 'rgba(220, 231, 251, 0.5)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {selectedOption?.label ?? placeholder ?? t(`Pick ${kind}`)}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            ml: 0.95,
                            width: 42,
                            height: 32,
                            borderRadius: '8px',
                            display: 'grid',
                            placeItems: 'center',
                            border: '1px solid rgba(255,255,255,0.12)',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                            flexShrink: 0
                        }}
                    >
                        <SearchRoundedIcon sx={{ fontSize: 19, color: '#edf3ff' }} />
                    </Box>
                </Button>
            ) : (
                <Button
                    fullWidth
                    variant="outlined"
                    onClick={openEditor}
                    sx={{
                        minHeight: 68,
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        borderRadius: 2.5,
                        px: 1.5,
                        py: 1.25,
                        borderColor: accentColor,
                        backgroundColor: 'rgba(9, 16, 29, 0.86)',
                        '&:hover': {
                            borderColor: accentColor,
                            backgroundColor: 'rgba(17, 27, 46, 0.86)'
                        }
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.05, minWidth: 0 }}>
                        {normalizedSlot.id ? (
                            <ItemIconFrame
                                iconUrl={selectedOption?.iconUrl}
                                itemType={kind}
                                badge={kind === 'troop' ? resolveSlotLevelValue(normalizedSlot, selectedOption) : ''}
                                amount={compactAmountLabel}
                                size={44}
                            />
                        ) : null}
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body1" sx={{ fontWeight: 700, color: '#dbe7ff', lineHeight: 1.15 }}>
                                {selectedOption?.label ?? t(`Pick ${kind}`)}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.35, color: '#b8c7e6', fontWeight: 700 }}>
                                {buildSlotMetaLabel(selectedOption, normalizedSlot, kind, t)}
                            </Typography>
                        </Box>
                    </Box>
                </Button>
            )}

            <Dialog
                open={open}
                onClose={closeEditor}
                fullWidth
                maxWidth={false}
                PaperProps={{
                    sx: {
                        width: 'min(calc(100vw - 32px), 312px)',
                        maxWidth: '312px',
                        minHeight: 208,
                        borderRadius: '12px',
                        background: 'linear-gradient(180deg, rgba(23,34,56,0.98) 0%, rgba(17,26,45,0.98) 100%)',
                        border: '1px solid rgba(125, 150, 190, 0.25)',
                        display: 'flex',
                        flexDirection: 'column'
                    }
                }}
            >
                <DialogTitle sx={{ px: 2.25, pt: 2, pb: 1.35 }}>
                    <Typography sx={{ fontSize: '1.1rem', lineHeight: 1.15, fontWeight: 800 }}>
                        {t('Edit Slot')}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ px: 2.25, pt: 0, pb: 1.25, display: 'flex', flexDirection: 'column', overflow: 'visible' }}>
                    <Autocomplete
                        openOnFocus
                        autoHighlight
                        freeSolo
                        disablePortal
                        options={draftOptions}
                        value={draftOption}
                        inputValue={String(draftSlot.id ?? '')}
                        loading={loading}
                        filterOptions={filterCatalogOptions}
                        getOptionLabel={option => typeof option === 'string' ? option : String(option?.value ?? '')}
                        isOptionEqualToValue={(option, nextValue) => option.value === nextValue.value}
                        onInputChange={(_, nextInputValue) => setDraftSlot(current => ({
                            ...current,
                            id: String(nextInputValue ?? '')
                        }))}
                        onChange={(_, nextOption) => setDraftSlot(current => ({
                            ...current,
                            id: typeof nextOption === 'string' ? nextOption : nextOption?.value ?? ''
                        }))}
                        renderOption={(props, option) => (
                            <Box
                                component="li"
                                {...props}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    py: 0.85
                                }}
                            >
                                <ItemIconFrame
                                    iconUrl={option.iconUrl}
                                    itemType={kind}
                                    badge={kind === 'troop' && Number(option.troopLevel) > 0 ? String(option.troopLevel) : ''}
                                    size={38}
                                />
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#eef3ff' }}>
                                        {option.label}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#93a8cf' }}>
                                        {`ID ${option.value}`}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                        renderInput={params => (
                            <TextField
                                {...params}
                                placeholder={t('Pick troop/tool ID')}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        minHeight: 40,
                                        borderRadius: '8px',
                                        backgroundColor: 'rgba(17, 26, 44, 0.92)',
                                        '& fieldset': {
                                            borderColor: 'rgba(255,255,255,0.14)'
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'rgba(255,255,255,0.2)'
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: 'rgba(89, 136, 220, 0.95)'
                                        }
                                    },
                                    '& .MuiInputBase-input': {
                                        color: '#dbe7ff',
                                        fontWeight: 600,
                                        fontSize: '0.95rem',
                                        py: 0.9
                                    }
                                }}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <InputAdornment position="end" sx={{ mr: 0 }}>
                                            <Box
                                                component="button"
                                                type="button"
                                                onClick={event => {
                                                    event.preventDefault()
                                                    event.stopPropagation()
                                                    openPicker()
                                                }}
                                                sx={{
                                                    width: 40,
                                                    height: 32,
                                                    borderRadius: '8px',
                                                    display: 'grid',
                                                    placeItems: 'center',
                                                    border: '1px solid rgba(255,255,255,0.12)',
                                                    backgroundColor: 'rgba(255,255,255,0.05)',
                                                    cursor: 'pointer',
                                                    p: 0,
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255,255,255,0.08)'
                                                    }
                                                }}
                                            >
                                                {loading ? <CircularProgress color="inherit" size={16} /> : <SearchRoundedIcon sx={{ fontSize: 19, color: '#edf3ff' }} />}
                                            </Box>
                                        </InputAdornment>
                                    )
                                }}
                                />
                        )}
                        noOptionsText={loading ? t(`Loading ${kind}s...`) : t(`No matching ${kind}s`)}
                    />

                    <TextField
                        fullWidth
                        placeholder={t('Amount')}
                        value={draftSlot.amount}
                        onChange={event => setDraftSlot(current => ({
                            ...current,
                            amount: event.target.value.replace(/[^\d]/g, '')
                        }))}
                        inputProps={{ inputMode: 'numeric' }}
                        sx={{
                            mt: 1.2,
                            '& .MuiOutlinedInput-root': {
                                minHeight: 40,
                                borderRadius: '8px',
                                backgroundColor: 'rgba(64, 82, 113, 0.66)',
                                '& fieldset': {
                                    borderColor: 'rgba(255,255,255,0.06)'
                                },
                                '&:hover fieldset': {
                                    borderColor: 'rgba(255,255,255,0.1)'
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'rgba(89, 136, 220, 0.95)'
                                }
                            },
                            '& .MuiInputBase-input': {
                                color: '#dbe7ff',
                                fontWeight: 600,
                                fontSize: '0.95rem',
                                py: 0.9
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 2.25, pb: 1.9, pt: 0.6, justifyContent: 'space-between', gap: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={closeEditor}
                        sx={{
                            minWidth: 0,
                            flex: 1,
                            minHeight: 36,
                            borderRadius: '10px',
                            fontSize: '0.95rem',
                            fontWeight: 700,
                            textTransform: 'none'
                        }}
                    >
                        {t('Cancel')}
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={removeValue}
                        sx={{
                            minWidth: 0,
                            flex: 1,
                            minHeight: 36,
                            borderRadius: '10px',
                            fontSize: '0.95rem',
                            fontWeight: 700,
                            textTransform: 'none',
                            boxShadow: 'none',
                            '&:hover': {
                                boxShadow: 'none'
                            }
                        }}
                    >
                        {t('Remove')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={saveEditor}
                        disabled={!String(draftSlot.id ?? '').trim()}
                        sx={{
                            minWidth: 0,
                            flex: 1,
                            minHeight: 36,
                            borderRadius: '10px',
                            fontSize: '0.95rem',
                            fontWeight: 700,
                            textTransform: 'none',
                            boxShadow: 'none',
                            '&:hover': {
                                boxShadow: 'none'
                            }
                        }}
                    >
                        {t('Save')}
                    </Button>
                </DialogActions>
            </Dialog>

            <TroopToolPickerDialog
                open={open && pickerOpen}
                options={pickerOptions}
                onClose={() => setPickerOpen(false)}
                onSelect={nextValue => setDraftSlot(current => ({
                    ...current,
                    id: String(nextValue ?? ''),
                    level: ''
                }))}
                appLanguage={appLanguage}
            />
        </>
    )
}

function CompactGroupLabel({ color, children }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.65, mb: 0.85 }}>
            <Box
                sx={{
                    width: 9,
                    height: 9,
                    borderRadius: '50%',
                    backgroundColor: color,
                    boxShadow: `0 0 0 3px ${color}22`
                }}
            />
            <Typography variant="caption" sx={{ color, fontWeight: 700, fontSize: '0.9rem' }}>
                {children}
            </Typography>
        </Box>
    )
}

function CompactLaneSection({ title, lane, troopOptions, toolOptions, loadingCatalog, onChange, onOpenSlotEditor, toolAccent, troopAccent, appLanguage = 'en' }) {
    const t = useAutoTranslate(appLanguage, [title, 'Tools', 'Troops'])

    return (
        <Box
            sx={{
                minHeight: 218,
                height: '100%',
                border: '1px solid rgba(137, 160, 198, 0.26)',
                borderRadius: '14px',
                p: '14px',
                backgroundColor: 'rgba(28, 41, 67, 0.42)'
            }}
        >
            <Typography
                variant="subtitle1"
                sx={{ textAlign: 'center', fontWeight: 700, color: '#f4f7ff', mb: 1.55, fontSize: '1.04rem' }}
            >
                {t(title)}
            </Typography>

            <CompactGroupLabel color={toolAccent}>
                {t('Tools')}
            </CompactGroupLabel>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${lane.tools.length}, minmax(86px, 1fr))`,
                    gap: 0.8,
                    mb: 1.45
                }}
            >
                {lane.tools.map((toolValue, index) => (
                    <SlotEditor
                        key={`${title}-compact-tool-${index}`}
                        slot={toolValue}
                        options={toolOptions}
                        kind="tool"
                        accentColor={toolAccent}
                        loading={loadingCatalog}
                        onOpenEditor={onOpenSlotEditor}
                        onChange={nextValue => onChange('tools', index, nextValue)}
                        pickerOptions={{ troops: troopOptions, tools: toolOptions }}
                        appLanguage={appLanguage}
                        displayMode="compact"
                        placeholder="T"
                    />
                ))}
            </Box>

            <CompactGroupLabel color={troopAccent}>
                {t('Troops')}
            </CompactGroupLabel>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${lane.troops.length > 4 ? 5 : lane.troops.length}, minmax(86px, 1fr))`,
                    gap: 0.8
                }}
            >
                {lane.troops.map((troopValue, index) => (
                    <SlotEditor
                        key={`${title}-compact-troop-${index}`}
                        slot={troopValue}
                        options={troopOptions}
                        kind="troop"
                        accentColor={troopAccent}
                        loading={loadingCatalog}
                        onOpenEditor={onOpenSlotEditor}
                        onChange={nextValue => onChange('troops', index, nextValue)}
                        pickerOptions={{ troops: troopOptions, tools: toolOptions }}
                        appLanguage={appLanguage}
                        displayMode="compact"
                        placeholder="U"
                    />
                ))}
            </Box>
        </Box>
    )
}

export default function AttackTemplateEditor({
    parentKey,
    userPlugins,
    onChange,
    presetOptions = [],
    libraryImportControls,
    selectedUser,
    availableInventory = [],
    appLanguage = 'en',
    templateValue,
    onTemplateChange
}) {
    const controlledMode = typeof onTemplateChange === 'function'
    const sourceTemplate = controlledMode ? templateValue : userPlugins?.[parentKey]?.[TEMPLATE_STORAGE_KEY]
    const [template, setTemplate] = React.useState(() =>
        normalizeTemplate(sourceTemplate))
    const [catalog, setCatalog] = React.useState(EMPTY_CATALOG)
    const [loadingCatalog, setLoadingCatalog] = React.useState(true)
    const [catalogError, setCatalogError] = React.useState('')
    const [refreshingInventory, setRefreshingInventory] = React.useState(false)
    const [liveInventory, setLiveInventory] = React.useState(() =>
        Array.isArray(availableInventory) ? availableInventory : [])
    const [expandedWaveIds, setExpandedWaveIds] = React.useState(() => [])
    const liveRefreshTimeoutRef = React.useRef(undefined)
    const lastLiveInventoryRequestAtRef = React.useRef(0)
    const t = useAutoTranslate(appLanguage, [
        'This builder now drives real attacks for this plugin. If you fill slots here, the legacy Attack Preset setting is ignored and only this template is used. You can still use Load from preset inside the builder as the base before your custom overrides, and troop/tool amounts are now clamped automatically to the current target-level limits.',
        'This template is saved with the plugin and can now be applied during real attacks for this module.',
        'Could not load troop and tool templates from the local server.',
        'Name',
        'Load from preset',
        'Description',
        'Attack Waves',
        'Default Wave',
        'Add Wave',
        'Wave',
        'Remove Wave',
        'Left',
        'Middle',
        'Right',
        'Support Tools',
        'Support Troops',
        'Attack Troops',
        'Defense Troops',
        'Attack Tools',
        'Defense Tools',
        'Only troops and tools currently available on this account are shown here. Saved selections remain visible until you change them.',
        'Refreshing live account inventory for this slot...',
        'Live account inventory is not available yet. Start the bot and open the slot again to see only valid troops and tools.',
        'Support Tools (AST)',
        'Support Wave (RW)',
        'Select tool',
        'Name is required',
        'Create Template',
        'Edit Template',
        'Source bot',
        'Select source bot',
        'Load preset from game',
        'Import into wave',
        'No live presets found on this bot.',
        'Loading presets from game...',
        'Set as default wave',
        'Offline'
    ])
    const isLibraryImportMode = Boolean(libraryImportControls)
    const sourceUserOptions = React.useMemo(
        () => Array.isArray(libraryImportControls?.sourceUserOptions) ? libraryImportControls.sourceUserOptions : [],
        [libraryImportControls?.sourceUserOptions]
    )
    const selectedSourceUserId = String(libraryImportControls?.selectedSourceUserId ?? '')
    const selectedImportPresetValue = String(libraryImportControls?.selectedImportPresetValue ?? '')
    const importPresetOptions = React.useMemo(
        () => Array.isArray(libraryImportControls?.importPresetOptions) ? libraryImportControls.importPresetOptions : [],
        [libraryImportControls?.importPresetOptions]
    )
    const importTargetWaveId = String(libraryImportControls?.importTargetWaveId ?? '')
    const loadingImportPresets = Boolean(libraryImportControls?.loadingImportPresets)
    const importPresetError = String(libraryImportControls?.importPresetError ?? '').trim()
    const selectedSourceUserOption = sourceUserOptions.find(option => String(option?.value ?? '') === selectedSourceUserId)
    const sourceUserPlaceholder = t('Select source bot')

    React.useEffect(() => {
        const nextTemplate = normalizeTemplate(sourceTemplate)
        setTemplate(currentTemplate => {
            return areTemplatesEqual(currentTemplate, nextTemplate) ?
                currentTemplate :
                nextTemplate
        })
    }, [sourceTemplate])

    React.useEffect(() => {
        setExpandedWaveIds(previous => {
            const validIds = new Set((template?.waves ?? []).map(wave => wave.id))
            const filteredIds = previous.filter(id => validIds.has(id))

            if (filteredIds.length > 0)
                return areSimpleArraysEqual(filteredIds, previous) ? previous : filteredIds

            const fallbackIds = template?.waves?.[0]?.id ? [template.waves[0].id] : []
            return areSimpleArraysEqual(fallbackIds, previous) ? previous : fallbackIds
        })
    }, [template?.waves])

    React.useEffect(() => {
        const nextInventory = Array.isArray(availableInventory) ? availableInventory : []
        setLiveInventory(currentInventory =>
            areInventoryArraysEqual(currentInventory, nextInventory) ? currentInventory : nextInventory)
        if (refreshingInventory)
            setRefreshingInventory(false)
    }, [availableInventory, refreshingInventory])

    React.useEffect(() => () => {
        if (liveRefreshTimeoutRef.current)
            clearTimeout(liveRefreshTimeoutRef.current)
    }, [])

    React.useEffect(() => {
        let ignore = false

        const loadCatalog = async () => {
            try {
                setLoadingCatalog(true)
                setCatalogError('')

                const response = await fetch(`/attack-template-catalog.json?language=${encodeURIComponent(appLanguage || 'en')}`)
                if (!response.ok)
                    throw new Error(`Catalog request failed with ${response.status}`)

                const nextCatalog = await response.json()
                if (ignore)
                    return

                setCatalog({
                    attackTroops: Array.isArray(nextCatalog.attackTroops) ? nextCatalog.attackTroops : [],
                    defenseTroops: Array.isArray(nextCatalog.defenseTroops) ? nextCatalog.defenseTroops : [],
                    attackTools: Array.isArray(nextCatalog.attackTools) ? nextCatalog.attackTools : [],
                    defenseTools: Array.isArray(nextCatalog.defenseTools) ? nextCatalog.defenseTools : []
                })
            } catch (error) {
                if (ignore)
                    return

                console.warn('Failed to load template catalog', error)
                setCatalogError('Could not load troop and tool templates from the local server.')
                setCatalog(EMPTY_CATALOG)
            } finally {
                if (!ignore)
                    setLoadingCatalog(false)
            }
        }

        loadCatalog()

        return () => {
            ignore = true
        }
    }, [appLanguage])

    const hasLiveInventory = Array.isArray(liveInventory) && liveInventory.length > 0
    const availableInventoryMap = React.useMemo(
        () => buildAvailableInventoryMap(liveInventory),
        [liveInventory]
    )
    const selectedTemplateIds = React.useMemo(
        () => collectSelectedTemplateIds(template),
        [template]
    )
    const filteredCatalog = React.useMemo(() =>
        filterCatalogByAvailability(catalog, availableInventoryMap, selectedTemplateIds, hasLiveInventory),
    [availableInventoryMap, catalog, hasLiveInventory, selectedTemplateIds])
    const optionLists = React.useMemo(
        () => toOptionList({
            attackTroops: attachOwnedAmount(filteredCatalog.attackTroops, availableInventoryMap),
            defenseTroops: attachOwnedAmount(filteredCatalog.defenseTroops, availableInventoryMap),
            attackTools: attachOwnedAmount(filteredCatalog.attackTools, availableInventoryMap),
            defenseTools: attachOwnedAmount(filteredCatalog.defenseTools, availableInventoryMap)
        }),
        [availableInventoryMap, filteredCatalog]
    )

    const requestLiveInventoryRefresh = React.useCallback(() => {
        const targetUserId = Number(selectedUser?.id)
        if (!Number.isFinite(targetUserId) || targetUserId <= 0)
            return Promise.resolve([])

        const now = Date.now()
        if (now - lastLiveInventoryRequestAtRef.current < 3000)
            return Promise.resolve(liveInventory)

        lastLiveInventoryRequestAtRef.current = now
        setRefreshingInventory(true)

        if (liveRefreshTimeoutRef.current)
            clearTimeout(liveRefreshTimeoutRef.current)

        liveRefreshTimeoutRef.current = setTimeout(() => {
            setRefreshingInventory(false)
        }, 7000)

        return fetch(`/attack-template-live-inventory/${targetUserId}`, {
            credentials: 'same-origin'
        })
            .then(async response => {
                if (!response.ok)
                    throw new Error(`Inventory request failed with ${response.status}`)

                const payload = await response.json()
                const nextInventory = Array.isArray(payload?.inventory) ? payload.inventory : []
                setLiveInventory(nextInventory)
                setRefreshingInventory(false)
                return nextInventory
            })
            .catch(error => {
                console.warn('Failed to refresh live attack template inventory', error)
                setRefreshingInventory(false)
                return liveInventory
            })
    }, [liveInventory, selectedUser?.id])

    const persistTemplate = React.useCallback((updater) => {
        const baseTemplate = normalizeTemplate(template)
        const nextTemplate = normalizeTemplate(
            typeof updater === 'function' ? updater(baseTemplate) : updater
        )

        setTemplate(currentTemplate =>
            areTemplatesEqual(currentTemplate, nextTemplate) ? currentTemplate : nextTemplate
        )

        if (controlledMode)
            onTemplateChange(nextTemplate)
        else {
            const nextPlugins = cloneValue(userPlugins ?? {})

            nextPlugins[parentKey] ??= {}
            nextPlugins[parentKey][TEMPLATE_STORAGE_KEY] = nextTemplate
            onChange(nextPlugins)
        }
    }, [controlledMode, onChange, onTemplateChange, parentKey, template, userPlugins])

    const handleFieldChange = (field, value) => {
        persistTemplate(current => ({ ...current, [field]: value }))
    }

    const handleLaneChange = (waveId, laneKey, groupKey, slotIndex, value) => {
        persistTemplate(current => ({
            ...current,
            waves: current.waves.map(wave => {
                if (wave.id !== waveId)
                    return wave

                const nextGroup = [...wave[laneKey][groupKey]]
                nextGroup[slotIndex] = normalizeSlot(value)

                return {
                    ...wave,
                    [laneKey]: {
                        ...wave[laneKey],
                        [groupKey]: nextGroup
                    }
                }
            })
        }))
    }

    const handleSupportToolChange = (index, value) => {
        persistTemplate(current => {
            const supportTools = [...current.supportTools]
            supportTools[index] = normalizeSlot(value)

            return {
                ...current,
                supportTools
            }
        })
    }

    const handleSupportWaveChange = (index, value) => {
        persistTemplate(current => {
            const supportWave = [...current.supportWave]
            supportWave[index] = normalizeSlot(value)

            return {
                ...current,
                supportWave
            }
        })
    }

    const addWave = () => {
        const nextWave = createWave(`Wave ${template.waves.length + 1}`)

        persistTemplate(current => {
            return {
                ...current,
                waves: [...current.waves, nextWave]
            }
        })

        setExpandedWaveIds(previous => [...previous, nextWave.id])
    }

    const removeWave = waveId => {
        persistTemplate(current => {
            const waves = current.waves.filter(wave => wave.id !== waveId)
            const nextWaves = waves.length > 0 ? waves : [createWave('Wave 1')]
            const defaultWaveId = nextWaves.some(wave => wave.id === current.defaultWaveId) ?
                current.defaultWaveId :
                nextWaves[0].id

            return {
                ...current,
                defaultWaveId,
                waves: nextWaves
            }
        })

        setExpandedWaveIds(previous => previous.filter(id => id !== waveId))
    }

    const toggleWaveExpanded = waveId => {
        setExpandedWaveIds(previous =>
            previous.includes(waveId) ?
                previous.filter(id => id !== waveId) :
                [...previous, waveId]
        )
    }

    const trimmedTemplateName = String(template?.name ?? '').trim()
    const nameMissing = !trimmedTemplateName

    return (
        <Box sx={{ display: 'grid', gap: 2.25 }}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2.15}
                    alignItems={{ xs: 'stretch', md: 'flex-start' }}
                    sx={{ width: 'fit-content', maxWidth: '100%' }}
                >
                    <Box sx={{ width: { xs: '100%', md: 241 } }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder={t('Name')}
                            value={template.name}
                            error={nameMissing}
                            onChange={event => handleFieldChange('name', event.target.value.slice(0, MAX_TEMPLATE_NAME_LENGTH))}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    minHeight: 39,
                                    borderRadius: '10px',
                                    backgroundColor: 'rgba(17, 26, 44, 0.92)',
                                    '& fieldset': {
                                        borderColor: nameMissing ? 'rgba(255, 74, 99, 0.95)' : 'rgba(255,255,255,0.14)'
                                    },
                                    '&:hover fieldset': {
                                        borderColor: nameMissing ? 'rgba(255, 74, 99, 0.95)' : 'rgba(255,255,255,0.2)'
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: nameMissing ? 'rgba(255, 74, 99, 0.95)' : 'rgba(89, 136, 220, 0.95)'
                                    }
                                }
                            }}
                        />
                        <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.85 }}>
                            <Typography variant="caption" sx={{ color: nameMissing ? '#ff5c70' : 'transparent', fontSize: '0.82rem' }}>
                                {nameMissing ? t('Name is required') : '.'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.82rem' }}>
                                {`${template.name.length}/${MAX_TEMPLATE_NAME_LENGTH}`}
                            </Typography>
                        </Stack>
                    </Box>

                    {isLibraryImportMode ? (
                        <Box sx={{ width: { xs: '100%', md: 241 } }}>
                            <FormControl fullWidth size="small">
                                <Select
                                    value={selectedSourceUserId}
                                    displayEmpty
                                    onChange={event => libraryImportControls?.onSourceUserChange?.(event.target.value)}
                                    renderValue={selectedValue =>
                                        selectedValue ?
                                            sourceUserOptions.find(option => String(option?.value ?? '') === String(selectedValue))?.label ?? selectedValue :
                                            sourceUserPlaceholder}
                                    sx={{
                                        minHeight: 39,
                                        borderRadius: '10px',
                                        backgroundColor: 'rgba(17, 26, 44, 0.92)',
                                        color: selectedSourceUserId ? 'text.primary' : 'text.secondary'
                                    }}
                                >
                                    {sourceUserOptions.map(option => (
                                        <MenuItem value={option.value} key={`library-source-user-${option.value}`}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Typography variant="caption" sx={{ mt: 0.85, display: 'block', color: 'text.secondary', fontSize: '0.82rem' }}>
                                {t('Source bot')}
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ width: { xs: '100%', md: 241 } }}>
                            <FormControl fullWidth size="small">
                                <Select
                                    value={template.presetSource}
                                    displayEmpty
                                    onChange={event => handleFieldChange('presetSource', event.target.value)}
                                    renderValue={selectedValue =>
                                        selectedValue ?
                                            presetOptions.find(option => option.value === selectedValue)?.label ?? selectedValue :
                                            t('Load from preset')}
                                    sx={{
                                        minHeight: 39,
                                        borderRadius: '10px',
                                        backgroundColor: 'rgba(17, 26, 44, 0.92)',
                                        color: template.presetSource ? 'text.primary' : 'text.secondary'
                                    }}
                                >
                                    {presetOptions.map((option, index) => (
                                        <MenuItem value={option.value} key={`library-preset-option-${index}`}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    )}

                    <Box sx={{ width: { xs: '100%', md: 240 } }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder={t('Description')}
                            value={template.description}
                            onChange={event => handleFieldChange('description', event.target.value.slice(0, MAX_TEMPLATE_DESCRIPTION_LENGTH))}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    minHeight: 39,
                                    borderRadius: '10px',
                                    backgroundColor: 'rgba(64, 82, 113, 0.66)'
                                }
                            }}
                        />
                        <Typography variant="caption" sx={{ mt: 0.85, display: 'block', textAlign: 'right', color: 'text.secondary', fontSize: '0.82rem' }}>
                            {`${template.description.length}/${MAX_TEMPLATE_DESCRIPTION_LENGTH}`}
                        </Typography>
                    </Box>
                </Stack>

                {catalogError && (
                    <Stack spacing={1}>
                        <Alert severity="warning" sx={{ borderRadius: 2 }}>
                            {t(catalogError)}
                        </Alert>
                    </Stack>
                )}

                {isLibraryImportMode && importPresetError && (
                    <Stack spacing={1}>
                        <Alert severity="warning" sx={{ borderRadius: 2 }}>
                            {importPresetError}
                        </Alert>
                    </Stack>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.25,
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#eef3ff', fontSize: '1.08rem' }}>
                        {t('Attack Waves')}
                    </Typography>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                        {isLibraryImportMode && (
                            <>
                                <FormControl size="small" sx={{ minWidth: 220 }}>
                                    <Select
                                        value={importTargetWaveId}
                                        displayEmpty
                                        onChange={event => libraryImportControls?.onImportTargetWaveChange?.(event.target.value)}
                                        renderValue={selectedValue =>
                                            template.waves.find(wave => wave.id === selectedValue)?.name ?? t('Import into wave')}
                                        sx={{
                                            minHeight: 37,
                                            borderRadius: '10px',
                                            backgroundColor: 'rgba(17, 26, 44, 0.92)'
                                        }}
                                    >
                                        {template.waves.map(wave => (
                                            <MenuItem value={wave.id} key={`library-import-target-wave-${wave.id}`}>
                                                {wave.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ minWidth: 240 }}>
                                    <Select
                                        value={selectedImportPresetValue}
                                        displayEmpty
                                        disabled={!selectedSourceUserId || loadingImportPresets || importPresetOptions.length <= 0}
                                        onChange={event => libraryImportControls?.onImportPresetSelect?.(event.target.value)}
                                        renderValue={selectedValue => {
                                            if (selectedValue) {
                                                return importPresetOptions.find(option => String(option?.value ?? '') === String(selectedValue))?.label ?? selectedValue
                                            }

                                            return loadingImportPresets ? t('Loading presets from game...') : t('Load preset from game')
                                        }}
                                        sx={{
                                            minHeight: 37,
                                            borderRadius: '10px',
                                            backgroundColor: 'rgba(17, 26, 44, 0.92)',
                                            color: selectedImportPresetValue ? 'text.primary' : 'text.secondary'
                                        }}
                                    >
                                        {importPresetOptions.length > 0 ? importPresetOptions.map(option => (
                                            <MenuItem
                                                value={option.value}
                                                key={`library-import-preset-${option.value}`}
                                                disabled={option.disabled === true}>
                                                {option.label}
                                            </MenuItem>
                                        )) : (
                                            <MenuItem value="" disabled>
                                                {selectedSourceUserOption ?
                                                    loadingImportPresets ?
                                                        t('Loading presets from game...') :
                                                        t('No live presets found on this bot.') :
                                                    sourceUserPlaceholder}
                                            </MenuItem>
                                        )}
                                    </Select>
                                </FormControl>
                            </>
                        )}
                        <Button
                            variant="outlined"
                            startIcon={<AddRoundedIcon />}
                            onClick={addWave}
                            sx={{
                                minHeight: 37,
                                px: 2.05,
                                borderRadius: '10px',
                                borderColor: 'rgba(0, 222, 121, 0.75)',
                                color: '#00e281',
                                backgroundColor: 'rgba(0, 226, 129, 0.08)',
                                '&:hover': {
                                    borderColor: 'rgba(0, 222, 121, 0.9)',
                                    backgroundColor: 'rgba(0, 226, 129, 0.14)'
                                }
                            }}
                        >
                            {t('Add Wave')}
                        </Button>
                    </Stack>
                </Box>

                <Box
                    sx={{
                        border: '1px solid rgba(137, 160, 198, 0.2)',
                        borderRadius: '14px',
                        backgroundColor: 'rgba(34, 47, 74, 0.42)',
                        overflow: 'hidden'
                    }}
                >
                    {template.waves.map((wave, index) => {
                        const expanded = expandedWaveIds.includes(wave.id)

                        return (
                            <Box key={wave.id}>
                                <Box
                                    sx={{
                                        px: 1.55,
                                        py: 1.2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 1
                                    }}
                                >
                                    <Button
                                        onClick={() => toggleWaveExpanded(wave.id)}
                                        startIcon={expanded ? <KeyboardArrowDownRoundedIcon /> : <KeyboardArrowRightRoundedIcon />}
                                        sx={{
                                            minWidth: 0,
                                            px: 0,
                                            py: 0,
                                            justifyContent: 'flex-start',
                                            color: 'text.primary',
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            fontSize: '1.02rem',
                                            '&:hover': {
                                                backgroundColor: 'transparent'
                                            }
                                        }}
                                    >
                                        {wave.name || `${t('Wave')} ${index + 1}`}
                                    </Button>

                                    <Stack direction="row" spacing={0.75} alignItems="center">
                                        <IconButton
                                            aria-label={t('Set as default wave')}
                                            onClick={() => handleFieldChange('defaultWaveId', wave.id)}
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '10px',
                                                color: template.defaultWaveId === wave.id ? '#ff8ea0' : 'text.secondary',
                                                border: '1px solid rgba(255,255,255,0.08)',
                                                backgroundColor: template.defaultWaveId === wave.id ?
                                                    'rgba(255, 82, 118, 0.14)' :
                                                    'rgba(255,255,255,0.02)',
                                                '&:hover': {
                                                    backgroundColor: template.defaultWaveId === wave.id ?
                                                        'rgba(255, 82, 118, 0.2)' :
                                                        'rgba(255,255,255,0.08)'
                                                }
                                            }}
                                        >
                                            {template.defaultWaveId === wave.id ? <FavoriteRoundedIcon /> : <FavoriteBorderRoundedIcon />}
                                        </IconButton>
                                        <Button
                                            variant="contained"
                                            color="error"
                                            disabled={template.waves.length <= 1}
                                            onClick={() => removeWave(wave.id)}
                                            sx={{
                                                minWidth: 40,
                                                width: 40,
                                                height: 40,
                                                p: 0,
                                                borderRadius: '10px',
                                                boxShadow: 'none',
                                                backgroundColor: 'rgba(180, 52, 74, 0.92)',
                                                '&:hover': {
                                                    boxShadow: 'none',
                                                    backgroundColor: 'rgba(204, 60, 84, 0.98)'
                                                }
                                            }}
                                        >
                                            <DeleteOutlineRoundedIcon sx={{ fontSize: 20 }} />
                                        </Button>
                                    </Stack>
                                </Box>

                                <Collapse in={expanded} timeout="auto" unmountOnExit={false}>
                                    <Box sx={{ px: 1.55, pb: 1.55 }}>
                                        <Grid container spacing={1.25}>
                                            <Grid item xs={12} lg={4}>
                                                <CompactLaneSection
                                                    title="Left"
                                                    lane={wave.left}
                                                    troopOptions={optionLists.troops}
                                                    toolOptions={optionLists.tools}
                                                    loadingCatalog={loadingCatalog || refreshingInventory}
                                                    onOpenSlotEditor={requestLiveInventoryRefresh}
                                                    toolAccent="#2f7cff"
                                                    troopAccent="#00cf71"
                                                    onChange={(groupKey, slotIndex, value) =>
                                                        handleLaneChange(wave.id, 'left', groupKey, slotIndex, value)}
                                                    appLanguage={appLanguage}
                                                />
                                            </Grid>
                                            <Grid item xs={12} lg={4}>
                                                <CompactLaneSection
                                                    title="Middle"
                                                    lane={wave.middle}
                                                    troopOptions={optionLists.troops}
                                                    toolOptions={optionLists.tools}
                                                    loadingCatalog={loadingCatalog || refreshingInventory}
                                                    onOpenSlotEditor={requestLiveInventoryRefresh}
                                                    toolAccent="#2f7cff"
                                                    troopAccent="#00cf71"
                                                    onChange={(groupKey, slotIndex, value) =>
                                                        handleLaneChange(wave.id, 'middle', groupKey, slotIndex, value)}
                                                    appLanguage={appLanguage}
                                                />
                                            </Grid>
                                            <Grid item xs={12} lg={4}>
                                                <CompactLaneSection
                                                    title="Right"
                                                    lane={wave.right}
                                                    troopOptions={optionLists.troops}
                                                    toolOptions={optionLists.tools}
                                                    loadingCatalog={loadingCatalog || refreshingInventory}
                                                    onOpenSlotEditor={requestLiveInventoryRefresh}
                                                    toolAccent="#2f7cff"
                                                    troopAccent="#00cf71"
                                                    onChange={(groupKey, slotIndex, value) =>
                                                        handleLaneChange(wave.id, 'right', groupKey, slotIndex, value)}
                                                    appLanguage={appLanguage}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Collapse>

                                {index < template.waves.length - 1 && (
                                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
                                )}
                            </Box>
                        )
                    })}
                </Box>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

                <Grid container spacing={1.5}>
                    <Grid item xs={12} lg={6}>
                        <Box
                            sx={{
                                minHeight: 154,
                                height: '100%',
                                border: '1px solid rgba(147, 77, 255, 0.3)',
                                borderRadius: '14px',
                                p: '15px 16px',
                                backgroundColor: 'rgba(38, 28, 57, 0.34)'
                            }}
                        >
                            <CompactGroupLabel color="#b55cff">
                                {t('Support Tools (AST)')}
                            </CompactGroupLabel>
                            <Grid container spacing={1}>
                                {template.supportTools.map((toolValue, index) => (
                                    <Grid item xs={12} md={4} key={`library-support-tool-${index}`}>
                                        <SlotEditor
                                            slot={toolValue}
                                            options={optionLists.tools}
                                            kind="tool"
                                            accentColor="#8b2cff"
                                            loading={loadingCatalog || refreshingInventory}
                                            onOpenEditor={requestLiveInventoryRefresh}
                                            onChange={nextValue => handleSupportToolChange(index, nextValue)}
                                            pickerOptions={{ troops: optionLists.troops, tools: optionLists.tools }}
                                            appLanguage={appLanguage}
                                            displayMode="support"
                                            placeholder={t('Select tool')}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </Grid>

                    <Grid item xs={12} lg={6}>
                        <Box
                            sx={{
                                minHeight: 154,
                                height: '100%',
                                border: '1px solid rgba(255, 138, 39, 0.28)',
                                borderRadius: '14px',
                                p: '15px 16px',
                                backgroundColor: 'rgba(57, 39, 24, 0.28)'
                            }}
                        >
                            <CompactGroupLabel color="#ff841f">
                                {t('Support Wave (RW)')}
                            </CompactGroupLabel>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(4, minmax(86px, 1fr))',
                                    gap: 1
                                }}
                            >
                                {template.supportWave.map((slotValue, index) => (
                                    <SlotEditor
                                        key={`library-support-wave-${index}`}
                                        slot={slotValue}
                                        options={optionLists.troops}
                                        kind="troop"
                                        accentColor="#00cf71"
                                        loading={loadingCatalog || refreshingInventory}
                                        onOpenEditor={requestLiveInventoryRefresh}
                                        onChange={nextValue => handleSupportWaveChange(index, nextValue)}
                                        pickerOptions={{ troops: optionLists.troops, tools: optionLists.tools }}
                                        appLanguage={appLanguage}
                                        displayMode="compact"
                                        placeholder="U"
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

                <Box sx={{ minHeight: 34 }} />
        </Box>
    )
}
